import type { Product } from "@/data/productDatabase";

export type VerificationStatus = "verified" | "corrected" | "needs-review";

export interface VerificationInfo {
  status: VerificationStatus;
  label: string;
  /** One-line reason shown in the badge tooltip. */
  reason: string;
}

const MIN_SPEC_FIELDS = 5;

/**
 * Resolves the data-integrity state of a listing.
 *
 * Explicit `product.verification` always wins (set during a manual audit).
 * Otherwise the status is derived from the evidence attached to the entry:
 * expert evidence, bench measurements, and rating confidence.
 */
export const getVerification = (product: Product): VerificationInfo => {
  if (product.verification === "corrected") {
    return {
      status: "corrected",
      label: "Corrected",
      reason: product.verificationNote ?? "A listing error was found and fixed during a catalog audit.",
    };
  }

  if (product.verification === "needs-review") {
    return {
      status: "needs-review",
      label: "Needs review",
      reason: product.verificationNote ?? "Flagged for manual source cross-check.",
    };
  }

  if (product.verification === "verified") {
    return {
      status: "verified",
      label: "Verified",
      reason:
        product.verificationNote ??
        `Specs cross-checked against manufacturer/authorized retailer data${product.lastUpdated ? ` (${product.lastUpdated})` : ""}.`,
    };
  }

  if (product.confidence === "low") {
    return {
      status: "needs-review",
      label: "Needs review",
      reason: "Low-confidence data — specs conflict across sources or are incomplete.",
    };
  }

  if (Object.keys(product.specs).length < MIN_SPEC_FIELDS) {
    return {
      status: "needs-review",
      label: "Needs review",
      reason: "Incomplete spec sheet — key fields are still missing.",
    };
  }

  if (product.benchTest) {
    return {
      status: "verified",
      label: "Verified",
      reason: `Independent bench measurements on file (${product.benchTest.source}).`,
    };
  }

  if (product.evidence && (product.confidence === "high" || product.confidence === "medium")) {
    return {
      status: "verified",
      label: "Verified",
      reason: `Specs and per-category evidence cross-checked against retailer/manufacturer sources${product.lastUpdated ? ` (${product.lastUpdated})` : ""}.`,
    };
  }

  return {
    status: "needs-review",
    label: "Needs review",
    reason: "Spec-only entry — awaiting cross-check against manufacturer or authorized retailer data.",
  };
};
